import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Dropdown,
  Flex,
  theme,
  Dropover,
} from '@xometry/xometry_loft';
import { css } from '@emotion/react';

export interface Props {
  width: string;
  height?: string;
  margin?: string;
  options: option[];

  tooltipText?: string;
}

type option = {
  id: number;
  optionText: string;
  itemCssStyleString?: string | undefined;
  textCssStyleString?: string | undefined;
  iconName?: string | undefined;
  iconCssStyleString?: string | undefined;
  handleClick: () => string | void;
};

const KebabMenu = (props: Props) => {
  const { options, width, height, margin, tooltipText } = props;
  const [hideTooltip, setHideTooltip] = useState(!tooltipText);
  const ref = useRef();

  useEffect(() => {
    if (ref.current && tooltipText) {
      const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.attributeName !== 'class') {
            continue;
          }
          if (/open/.test((mutation.target as any)?.classList)) {
            setHideTooltip(true);
          } else if (/closed/.test((mutation.target as any)?.classList)) {
            setHideTooltip(false);
          }
        }
      });
      observer.observe(ref.current, {
        attributes: true,
        characterData: true,
        childList: true,
        subtree: true,
      });
      return () => observer.disconnect();
    }
  }, [tooltipText, ref]);

  return (
    <Dropdown alignment="right">
      <Dropdown.Trigger>
        <Dropover alignment="center" placement="top">
          <Dropover.Trigger>
            <Button
              data-testid="kebab-menu-btn"
              aria-label="dropdown menu"
              kind="text"
              css={css`
                padding: ${theme.space[2]} ${theme.space[2]};
              `}
            >
              <Flex alignItems="center">
                <i className="far fa-ellipsis-h" />
              </Flex>
            </Button>
          </Dropover.Trigger>
          <Dropover.Content
            width="300px"
            style={{
              fontWeight: 'initial',
              ...(hideTooltip ? { display: 'none' } : {}),
            }}
          >
            {tooltipText}
          </Dropover.Content>
        </Dropover>
      </Dropdown.Trigger>
      <div ref={ref}>
        <Dropdown.Content width={width} height={height} margin={margin}>
          {options?.map((menuItem: any) => (
            <Dropdown.Option
              key={menuItem.id}
              css={css`
                padding: ${theme.space[2]} 0;
              `}
            >
              <Flex
                color={theme.colors.darkBlues[50]}
                onClick={menuItem.handleClick}
                css={css`
                  ${menuItem.itemCssStyleString}
                `}
              >
                <i
                  className={`${menuItem.iconName}`}
                  css={css`
                    ${menuItem.iconCssStyleString}
                  `}
                />
                <Box
                  css={css`
                    ${menuItem.textCssStyleString}
                  `}
                >
                  {menuItem.optionText}
                </Box>
              </Flex>
            </Dropdown.Option>
          ))}
        </Dropdown.Content>
      </div>
    </Dropdown>
  );
};

export default KebabMenu;
